import { Link } from 'react-router-dom';
import type { ClassRoom, TimetableSlot } from '@/lib/types';
import { DAY_NAMES, formatTime } from '@/lib/constants';

type Props = {
  slots: TimetableSlot[];
  classMap: Map<string, ClassRoom>;
  studentsBySlot: Record<string, string[]>;
  teacherMap: Map<string, string>;
  showTeacher: boolean;
  onEdit: (slot: TimetableSlot) => void;
};

export default function WeeklyTimetable({
  slots,
  classMap,
  studentsBySlot,
  teacherMap,
  showTeacher,
  onEdit,
}: Props) {
  const days = [1, 2, 3, 4, 5, 6, 0];

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
      {days.map((day) => {
        const daySlots = slots
          .filter((s) => s.day_of_week === day)
          .sort((a, b) => a.start_time.localeCompare(b.start_time));
        const isToday = new Date().getDay() === day;

        return (
          <div
            key={day}
            className={`rounded-lg border bg-white ${
              isToday ? 'border-accent-300' : 'border-background-200'
            }`}
          >
            <div
              className={`rounded-t-lg px-3 py-2 text-center text-sm font-bold ${
                isToday
                  ? 'bg-accent-500 text-background-50'
                  : 'bg-background-50 text-foreground-700'
              }`}
            >
              {DAY_NAMES[day]}요일
              {isToday && <span className="ml-1 text-[10px] font-normal">오늘</span>}
            </div>
            <div className="space-y-2 p-2">
              {daySlots.length === 0 ? (
                <p className="py-6 text-center text-xs text-foreground-300">-</p>
              ) : (
                daySlots.map((slot) => {
                  const c = classMap.get(slot.class_id);
                  const students = studentsBySlot[slot.id] || [];
                  const teacherName = c?.teacher_id
                    ? teacherMap.get(c.teacher_id) || '담당 미배정'
                    : '담당 미배정';
                  return (
                    <div
                      key={slot.id}
                      className="group relative rounded-md border border-primary-200 bg-primary-50 p-2.5 transition-colors hover:bg-primary-100"
                    >
                      <button
                        onClick={() => onEdit(slot)}
                        title="수업 편집"
                        className="absolute right-1.5 top-1.5 z-10 flex h-6 w-6 cursor-pointer items-center justify-center rounded-md text-foreground-400 opacity-0 transition-opacity hover:bg-white hover:text-foreground-900 group-hover:opacity-100"
                      >
                        <i className="ri-pencil-line text-xs"></i>
                      </button>
                      <Link
                        to={c ? `/classes/${c.id}` : '/classes'}
                        state={{ from: 'timetable' }}
                        onClick={() =>
                          sessionStorage.setItem('classDetailFrom', 'timetable')
                        }
                        className="block cursor-pointer"
                      >
                        <p className="truncate pr-6 text-sm font-semibold text-foreground-900">
                          {c?.name || '삭제된 반'}
                        </p>
                        {c?.subject && (
                          <p className="truncate text-xs text-foreground-500">{c.subject}</p>
                        )}
                        {showTeacher && (
                          <p className="mt-1 flex items-center gap-1 truncate text-[11px] text-secondary-700">
                            <i className="ri-user-line"></i>
                            {teacherName}
                          </p>
                        )}
                        <p className="mt-1 text-xs font-medium text-primary-700">
                          {formatTime(slot.start_time)} ~ {formatTime(slot.end_time)}
                        </p>
                        {students.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1 border-t border-primary-100 pt-1.5">
                            {students.map((name) => (
                              <span
                                key={name}
                                className="rounded-full bg-white px-2 py-0.5 text-xs font-medium leading-5 text-foreground-700"
                              >
                                {name}
                              </span>
                            ))}
                          </div>
                        )}
                      </Link>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}